import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Loader2, FileText, ArrowLeft, BarChart3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { useNavigate } from 'react-router-dom';
import { invoicesService, type Invoice } from '@/services/invoicesService';
import InvoiceEditor from '@/components/invoicing/InvoiceEditor';
import { INVOICE_STATUS_COLORS, formatCents } from '@/lib/invoiceFormat';
import { InvoiceSourceBadge } from '@/components/invoicing/InvoiceSourceBadge';

export default function InvoicesPage() {
  const { clinic } = useClinic();
  const { toast } = useToast();
  const navigate = useNavigate();
  const clinicId = clinic?.id;

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [showEditor, setShowEditor] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const fetchInvoices = useCallback(async () => {
    if (!clinicId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await invoicesService.list(clinicId, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
        page,
        limit: 25,
      });
      setInvoices(result.invoices);
      setTotal(result.total);
    } catch {
      toast({ title: 'Error', description: 'Failed to load invoices', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [clinicId, statusFilter, sourceFilter, page, toast]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleCreateNew = () => {
    setSelectedInvoice(null);
    setShowEditor(true);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setSelectedInvoice(null);
    fetchInvoices();
  };

  const handleVoid = async (invoiceId: string, reason?: string): Promise<Invoice | null> => {
    if (!clinicId) return null;
    try {
      const updated = await invoicesService.void(clinicId, invoiceId, reason);
      toast({ title: 'Voided', description: 'Invoice has been voided' });
      setSelectedInvoice((prev) => (prev && prev.id === invoiceId ? { ...prev, ...updated } : prev));
      fetchInvoices();
      return updated;
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || 'Failed to void invoice';
      toast({ title: 'Error', description: message, variant: 'destructive' });
      return null;
    }
  };

  const handleDownloadPdf = async (invoiceId: string) => {
    if (!clinicId) return;
    try {
      const { pdfUrl } = await invoicesService.getPdf(clinicId, invoiceId);
      if (!pdfUrl) throw new Error('PDF URL was empty');
      window.open(pdfUrl, '_blank');
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || err?.message || 'Failed to generate PDF';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  if (showEditor) {
    return (
      <InvoiceEditor
        clinicId={clinicId!}
        invoice={selectedInvoice}
        onClose={handleEditorClose}
        onVoid={handleVoid}
        onDownloadPdf={handleDownloadPdf}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Invoices</h1>
            <p className="text-muted-foreground">Per-visit billing and superbills</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/invoices/dashboard')}>
            <BarChart3 className="mr-2 h-4 w-4" />
            Dashboard
          </Button>
          <Button onClick={handleCreateNew}>
            <Plus className="mr-2 h-4 w-4" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="partially_paid">Partially Paid</SelectItem>
            <SelectItem value="void">Void</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="auto_from_booking">Appointment</SelectItem>
            <SelectItem value="auto_from_encounter">Encounter</SelectItem>
            <SelectItem value="auto_from_prescription">Prescription</SelectItem>
            <SelectItem value="auto_from_lab_order">Lab Order</SelectItem>
            <SelectItem value="subscription_refill">Subscription</SelectItem>
            <SelectItem value="amendment_delta">Amendment</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoice Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No invoices yet</h3>
            <p className="text-muted-foreground mb-4">Create your first invoice for a patient</p>
            <Button onClick={handleCreateNew}>
              <Plus className="mr-2 h-4 w-4" />
              New Invoice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleViewInvoice(inv)}
                >
                  <TableCell className="font-medium">
                    {inv.invoice_number || 'DRAFT'}
                  </TableCell>
                  <TableCell>
                    {inv.patient
                      ? [inv.patient.first_name, inv.patient.middle_name, inv.patient.last_name]
                          .filter(Boolean)
                          .join(' ')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {inv.issued_at
                      ? new Date(inv.issued_at).toLocaleDateString()
                      : new Date(inv.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge className={`capitalize ${INVOICE_STATUS_COLORS[inv.status] || 'bg-gray-100'}`}>
                      {inv.status.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <InvoiceSourceBadge source={inv.source} invoice={inv} />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCents(inv.total_cents)}
                  </TableCell>
                  <TableCell className="text-right">
                    {inv.balance_cents > 0 ? (
                      <span className="text-red-600 font-medium">{formatCents(inv.balance_cents)}</span>
                    ) : (
                      <span className="text-green-600">$0.00</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewInvoice(inv);
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {total > 25 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * 25 + 1}–{Math.min(page * 25, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page * 25 >= total} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
